import React from 'react';
import { motion } from 'framer-motion';
import { Github, Linkedin, Code, Download, ChevronDown, ExternalLink, Sparkles, Mail, Layers } from 'lucide-react';
import { Link } from "react-router-dom";
import assets from '../../assets/assets.js';

const Home = () => {

  return (
    <div className="min-h-screen bg-[#0b0f19] overflow-hidden">

      {/* Background  */}
      <div className="fixed inset-0 z-0">
        <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] bg-white/5 rounded-full blur-[120px] animate-pulse" />
        <div className="absolute top-[20%] right-[-10%] w-[400px] h-[400px] bg-black/10 rounded-full blur-[100px] animate-pulse delay-1000" />
        <div className="absolute bottom-[-10%] left-[20%] w-[600px] h-[600px] bg-white/5 rounded-full blur-[120px] animate-pulse delay-2000" />
      </div>

      {/* Hero Section */}
      <section className="relative z-10 min-h-screen flex flex-col justify-center items-center px-4 sm:px-6 lg:px-8 pt-20">

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center max-w-6xl mx-auto"
        >
          {/* Status Badge */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex items-center space-x-2 bg-white/10 border border-white/20 rounded-full px-5 py-2 backdrop-blur-md hover:border-white/40 transition-colors shadow-sm">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
              </span>
              <span className="text-white text-[10px] font-black tracking-widest uppercase">Available for Work</span>
            </div>
          </div>

          {/* Main Heading */}
          <h1 className="text-5xl md:text-8xl lg:text-9xl font-black tracking-tighter mb-8 leading-[0.9] text-white">
            Building Digital
            <br />
            <span className="gradient-text">
              Realities.
            </span>
          </h1>

          <p className="text-xl md:text-2xl text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed font-medium">
            <span className="text-white font-bold underline decoration-white/30 underline-offset-8">Pratik Patil</span>, a Full-Stack Engineer crafting high-performance applications with cutting-edge <span className="text-white">AI Intelligence</span>.
          </p>

          {/* Buttons */}
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
            <Link
              to="/project"
              className="px-10 py-5 bg-white text-[#0b0f19] rounded-2xl font-black text-lg hover:bg-gray-100 transition-all transform hover:scale-105 shadow-2xl shadow-white/30 flex items-center gap-2 group"
            >
              Explore Projects <ExternalLink size={20} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <a
              href="https://drive.google.com/file/d/18bgj928lPpVmvy3e1WybNrCRyYOgaOnG/view?usp=sharing"
              target="_blank"
              rel="noopener noreferrer"
              className="px-10 py-5 bg-white/5 border border-white/10 text-white rounded-2xl font-black text-lg hover:bg-white/10 hover:border-white/50 transition-all transform hover:scale-105 backdrop-blur-md flex items-center gap-2"
            >
              Download CV <Download size={20} />
            </a>
          </div>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 1 }}
          className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce cursor-pointer text-gray-500 hover:text-white transition-colors"
        >
          <ChevronDown className="w-10 h-10" />
        </motion.div>
      </section>

      {/* About Section */}
      <section className="relative z-10 py-32 px-4 sm:px-6 lg:px-8 max-w-[1400px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">

          {/* Image Side */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative group order-2 lg:order-1"
          >
            <div className="absolute -inset-1 bg-gradient-to-r from-white to-gray-400 rounded-[3rem] blur-xl opacity-20 group-hover:opacity-40 transition duration-1000"></div>
            <div className="relative rounded-[3rem] overflow-hidden aspect-[4/5] lg:aspect-square bg-[#111928] border border-white/5 shadow-2xl">
              <img
                src={assets.pratik_photo}
                alt="Pratik Patil"
                className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-1000 scale-105 group-hover:scale-100"
              />

              {/* Floating Cards */}
              <div className="absolute bottom-8 left-8 bg-[#0b0f19]/90 backdrop-blur-2xl p-5 rounded-2xl border border-white/10 shadow-2xl transform group-hover:translate-y-[-10px] transition-all duration-500">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-white/20 rounded-xl text-white shadow-inner">
                    <Code size={24} />
                  </div>
                  <div>
                    <p className="text-gray-400 text-[10px] font-black tracking-widest uppercase mb-1">LeetCode</p>
                    <p className="text-white font-black text-2xl tracking-tighter">800+</p>
                  </div>
                </div>
              </div>

              <div className="absolute top-8 right-8 bg-[#0b0f19]/90 backdrop-blur-2xl p-5 rounded-2xl border border-white/10 shadow-2xl transform group-hover:translate-y-[10px] transition-all duration-500 delay-100">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-white/20 rounded-xl text-white shadow-inner">
                    <Sparkles size={24} />
                  </div>
                  <div>
                    <p className="text-gray-400 text-[10px] font-black tracking-widest uppercase mb-1">CGPA</p>
                    <p className="text-white font-black text-2xl tracking-tighter">8.87</p>
                  </div>
                </div>
              </div>

            </div>
          </motion.div>

          {/* Content Side */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="order-1 lg:order-2"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-full mb-8">
              <Layers className="h-4 w-4 text-white" />
              <span className="text-white text-[10px] font-black tracking-widest uppercase">Engineer</span>
            </div>
            <h2 className="text-5xl md:text-7xl font-black text-white mb-8 tracking-tighter leading-[0.9]">
              Engineering for{' '}
              <span className="text-white">
                Performance.
              </span>
            </h2>

            <div className="space-y-8 text-gray-300 text-lg md:text-xl leading-relaxed font-medium opacity-80">
              <p>
                Hello! I'm <span className="text-white font-bold">Pratik Patil</span>, an engineer specializing in architecting high-performance systems and full-stack solutions.
              </p>
              <p>
                Driven by complex problem-solving, I've solved <span className="text-white font-black underline decoration-white/40">800+ algorithmic challenges</span>. I focus on writing clean, scalable code that bridges the gap between AI and core engineering.
              </p>
            </div>

            <div className="mt-12 flex flex-wrap gap-5">
              {[
                { label: "GitHub", icon: Github, link: "https://github.com/Pratik9113", color: "text-white" },
                { label: "LinkedIn", icon: Linkedin, link: "https://www.linkedin.com/in/pratik-patil-b5681424b/", color: "text-white" },
                { label: "Email", icon: Mail, link: "mailto:pratik.patil9113@gmail.com", color: "text-white" },
              ].map((item) => (
                <a
                  key={item.label}
                  href={item.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-4 bg-white/5 hover:bg-white/10 rounded-2xl border border-white/10 hover:border-white/50 transition-all text-white group shadow-lg"
                >
                  <item.icon size={26} className={`group-hover:${item.color} transition-colors`} />
                </a>
              ))}
            </div>
          </motion.div>

        </div>
      </section>
    </div>
  );
};

export default Home;